import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UsersService } from '../../services/users.service';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { ConfigService } from '../../services/config.service';
import { filterProductBrandsForSalesperson, showSalespersonBrandScope } from '../../utils/salesperson-brand-scope';
import { SalesmanCreateAppUserDialogComponent } from './salesman-create-app-user-dialog.component';
import type { CompanyLocation, PortalBrandOption, SalesmanSyncRow, UserGroupOption } from './salesman-app-users.model';

@Component({
	selector: 'app-salesman-app-users',
	templateUrl: './salesman-app-users.component.html',
	styleUrls: ['./salesman-app-users.component.scss']
})
export class SalesmanAppUsersComponent implements OnInit {
	title = 'Salesperson → app users';
	color = '';
	loading = false;
	rows: SalesmanSyncRow[] = [];
	filterCtrl = new FormControl<'all' | 'needs_user' | 'has_user'>('all');
	preparingDialog = false;

	constructor(
		private usersService: UsersService,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private configService: ConfigService,
		private router: Router,
		private dialog: MatDialog
	) {
		this.color = this.globalSearchService.getColor();
	}

	get showBrandScope(): boolean {
		return showSalespersonBrandScope(this.configService.getConfig());
	}

	ngOnInit(): void {
		this.refresh();
	}

	get filteredRows(): SalesmanSyncRow[] {
		const f = this.filterCtrl.value || 'all';
		return this.rows.filter((r) => {
			if (f === 'needs_user') {
				return !r.has_app_user;
			}
			if (f === 'has_user') {
				return r.has_app_user;
			}
			return true;
		});
	}

	refresh(): void {
		this.loading = true;
		forkJoin({
			salesmen: this.globalsService.getSalesmanList(),
			users: this.usersService.getUsers(),
			brandMap: this.showBrandScope ? this.globalsService.getSalesmanPortalBrands() : of([]),
		}).subscribe({
			next: ({ salesmen, users, brandMap }: { salesmen: any; users: any; brandMap: any }) => {
				const salesRows = Array.isArray(salesmen) ? salesmen : [];
				const userRows = Array.isArray(users) ? users : [];
				const brandsByCode = new Map<string, any>();
				(Array.isArray(brandMap) ? brandMap : []).forEach((b: any) => {
					const code = ((b?.salesmancode as string) || '').trim();
					if (code) {
						brandsByCode.set(code, b);
					}
				});
				const usersBySalesCode = new Map<string, string>();
				const existingUserids = new Set<string>();

				userRows.forEach((u: any) => {
					const uid = ((u?.userid as string) || '').trim();
					const scode = ((u?.salesman as string) || '').trim();
					if (uid) {
						existingUserids.add(uid.toLowerCase());
					}
					if (scode && uid && !usersBySalesCode.has(scode)) {
						usersBySalesCode.set(scode, uid);
					}
				});

				this.rows = salesRows.map((s: any) => {
					const code = ((s?.salesmancode as string) || '').trim();
					const appUserid = usersBySalesCode.get(code) || null;
					const cr = this.pickFirstNumber(s, ['commissionrate1', 'COMMISSIONRATE1']);
					const cur = this.pickFirstNumber(s, ['current', 'CURRENT']);
					const email = this.pickStr(s, ['smanemail', 'extra_smanemail', 'SMANEMAIL', 'EXTRA_SMANEMAIL']);
					const loginRaw = this.pickStr(s, ['smanlogin', 'extra_smanlogin', 'SMANLOGIN']);
					const login = loginRaw.trim().toLowerCase();
					const pass = this.pickStr(s, ['smanpass', 'extra_smanpass', 'SMANPASS']);
					const loginOk = /^[a-z0-9._-]+$/.test(login) && login.length > 0 && !existingUserids.has(login);
					const suggestedUserid = appUserid
						? null
						: loginOk
							? login.toUpperCase()
							: this.firstAvailableUserid(this.baseUseridFromName((s?.salesmanname as string) || '', code), existingUserids);
					const brandInfo = brandsByCode.get(code);
					const brandList: any[] = Array.isArray(brandInfo?.brands) ? brandInfo.brands : [];
					const defaultBrand = brandList.find((b) => b?.default);
					return {
						salesmancode: code,
						salesmanname: (s?.salesmanname as string) || '',
						current: cur !== null ? cur : 1,
						commissionrate1: cr !== null ? cr : 0,
						smanemail: email,
						smanlogin: login,
						smanpass: pass,
						defaultloc: ((s?.defaultloc as string) || '').trim(),
						eligible_for_app_login: true,
						has_app_user: !!appUserid,
						app_userid: appUserid,
						suggested_userid: suggestedUserid,
						portal_manufacturer_ids: brandList.map((b) => Number(b.manufacturer_id)).filter((n) => n > 0),
						portal_default_manufacturer_id: defaultBrand ? Number(defaultBrand.manufacturer_id) : null,
						portal_brand_count: brandList.length,
						portal_brand_names: brandList.map((b) => b.name).filter((n) => !!n).join(', '),
						portal_default_brand_name: defaultBrand ? defaultBrand.name : '',
					} as SalesmanSyncRow;
				});
				this.loading = false;
			},
			error: () => {
				this.loading = false;
				this.globalSearchService.showNotification('Could not load salesperson list', 'danger', 'bottom', 'right');
			}
		});
	}

	openCreateDialog(row: SalesmanSyncRow): void {
		if (row.has_app_user) {
			return;
		}
		this.preparingDialog = true;
		const withBrands = this.showBrandScope;
		forkJoin({
			groups: this.usersService.getUserGroups(),
			locations: this.globalsService.getAllLocations(),
			brands: withBrands ? this.inventoryService.getItemManufacturer() : of([]),
		}).subscribe({
			next: ({ groups, locations, brands }) => {
				this.preparingDialog = false;
				const usergroups = this.normalizeUserGroups(groups);
				const locs = this.normalizeLocations(locations);
				const brandOptions: PortalBrandOption[] = withBrands ? filterProductBrandsForSalesperson(brands as any) : [];
				// row.portal_manufacturer_ids / _default were populated when the list loaded.
				const ref = this.dialog.open(SalesmanCreateAppUserDialogComponent, {
					width: 'min(520px, 100vw - 32px)',
					maxWidth: '95vw',
					autoFocus: 'first-tabbing-element',
					restoreFocus: true,
					data: {
						row,
						usergroups,
						locations: locs,
						brands: brandOptions,
						showBrandScope: withBrands,
					},
				});
				ref.afterClosed().subscribe((result) => {
					if (result?.created && result.userid) {
						this.router.navigate(['/users/edit', result.userid]);
					}
				});
			},
			error: () => {
				this.preparingDialog = false;
				this.globalSearchService.showNotification(
					'Could not load user groups or locations for the form.',
					'danger',
					'bottom',
					'right'
				);
			},
		});
	}

	private normalizeUserGroups(raw: unknown): UserGroupOption[] {
		const arr = Array.isArray(raw) ? raw : [];
		return [...arr]
			.map((g: any) => ({
				user_group_id: g.user_group_id,
				name: String(g.name ?? ''),
				user_count: g.user_count != null ? Number(g.user_count) : undefined,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	private normalizeLocations(raw: unknown): CompanyLocation[] {
		const arr = Array.isArray(raw) ? raw : [];
		return [...arr]
			.map((l: any) => ({
				loccode: String(l.loccode ?? '').trim(),
				locationname: l.locationname != null ? String(l.locationname) : undefined,
				orignalname: l.orignalname != null ? String(l.orignalname) : undefined,
			}))
			.filter((l) => l.loccode !== '')
			.sort((a, b) => a.loccode.localeCompare(b.loccode));
	}

	openEdit(userid: string): void {
		this.router.navigate(['/users/edit', userid]);
	}

	statusLabel(row: SalesmanSyncRow): string {
		if (row.has_app_user) {
			return 'Has app user';
		}
		return 'Needs app user';
	}

	private baseUseridFromName(fullName: string, fallbackCode = ''): string {
		const cleaned = (fullName || '').trim().toLowerCase();
		if (!cleaned) {
			return fallbackCode ? `salesman_${fallbackCode.toLowerCase().replace(/[^a-z0-9]/g, '')}` : 'salesman';
		}
		const parts = cleaned.split(/\s+/).filter((p) => p.length > 0);
		const first = (parts[0] || '').replace(/[^a-z0-9]/g, '');
		const last = (parts.length > 1 ? parts[parts.length - 1] : '').replace(/[^a-z0-9]/g, '');
		const base = last ? `${first}.${last}` : first;
		return base || (fallbackCode ? `salesman_${fallbackCode.toLowerCase().replace(/[^a-z0-9]/g, '')}` : 'salesman');
	}

	private pickStr(obj: any, keys: string[]): string {
		for (const k of keys) {
			const v = obj?.[k];
			if (v != null && String(v).trim() !== '') {
				return String(v).trim();
			}
		}
		return '';
	}

	private pickFirstNumber(obj: any, keys: string[]): number | null {
		for (const k of keys) {
			const v = obj?.[k];
			if (v === null || v === undefined || v === '') {
				continue;
			}
			const n = Number(String(v).trim().replace(',', '.'));
			if (Number.isFinite(n)) {
				return n;
			}
		}
		return null;
	}

	private firstAvailableUserid(base: string, existing: Set<string>): string {
		const norm = (base || 'salesman').toLowerCase().replace(/[^a-z0-9._-]/g, '');
		if (!existing.has(norm)) {
			return norm.toUpperCase();
		}
		for (let i = 1; i <= 9999; i++) {
			const candidate = `${norm}${String(i).padStart(2, '0')}`;
			if (!existing.has(candidate)) {
				return candidate.toUpperCase();
			}
		}
		return `${norm}${Date.now().toString(36)}`.toUpperCase();
	}
}
